/**
 * <rail-slider> - forrige/naeste-pile til temaets vandrette scroll-snap-spor.
 *
 * Delt af certificates (PDP) og featured-collection; editorial-carousel kan
 * tage den uden ny JS. Var certificate-slider.js indtil 2026-09-18, hvor
 * featured-collection skulle have samme kontroller - generaliseret frem for
 * kopieret, fordi to kopier af den samme test allerede er drevet fra hinanden
 * i dette tema (se snippets/product-is-new.liquid).
 *
 * Elementerne findes paa DATA-ATTRIBUTTER og ikke paa klassenavne, saa hver
 * sektion beholder sin egen BEM-navngivning:
 *   [data-rail-track] - selve scroll-sporet (paakraevet)
 *   [data-rail-prev] / [data-rail-next] - pileknapperne (paakraevet)
 *   [data-rail-item]  - én flise; bredden er skridtlaengden (paakraevet)
 *
 * Progressive enhancement: sporet er et native scroll-snap-spor, der kan
 * scrolles med touch, trackpad og piletaster uden JS, og fremdriftsstriben er
 * ren CSS (scroll-timeline). Denne komponent tilfoejer kun det, en almindelig
 * mus mangler: pile, der flytter én flise ad gangen. Pilene er skjulte i
 * markuppen og vises foerst, naar sporet faktisk scroller.
 *
 * Endestop markeres med aria-disabled, ikke disabled, saa tastaturfokus ikke
 * tabes, naar man klikker sig til enden. Tilstanden opdateres ved scroll og
 * resize (ResizeObserver paa sporet, som ogsaa fanger at en udfoldet tekst
 * aendrer hoejden), samlet i én rAF. Sporets tabindex fjernes, naar det ikke
 * scroller, saa desktop med 2-3 fliser ikke faar et doedt tabstop.
 *
 * Elementet reconnectes selv ved shopify:section:load; lyttere og observer
 * ryddes i disconnectedCallback.
 */
class RailSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.track || !this.prev || !this.next) return;

    this.onPrev = () => this.#step(-1);
    this.onNext = () => this.#step(1);
    // scrollend rydder maalet, ogsaa hvis brugeren afbryder en animation med
    // et swipe; i browsere uden scrollend goer #update det ved maalet.
    this.onScrollEnd = () => {
      this.target = undefined;
    };
    this.onChange = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.#update());
    };

    this.prev.addEventListener('click', this.onPrev);
    this.next.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onChange, { passive: true });
    this.track.addEventListener('scrollend', this.onScrollEnd);
    this.ro = new ResizeObserver(this.onChange);
    this.ro.observe(this.track);
    this.#update();
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    cancelAnimationFrame(this.frame);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onChange);
    this.track?.removeEventListener('scrollend', this.onScrollEnd);
  }

  #update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrolls = max > 1;
    this.prev.hidden = !scrolls;
    this.next.hidden = !scrolls;
    // Maalt tilstand, saa CSS kan skjule fx striben naar der intet er at
    // scrolle - uden at gaette paa antal fliser.
    this.toggleAttribute('data-rail-scrolls', scrolls);
    if (scrolls) {
      this.track.setAttribute('tabindex', '0');
    } else {
      this.track.removeAttribute('tabindex');
    }
    // Math.abs: i RTL er scrollLeft negativ.
    const position = Math.abs(this.track.scrollLeft);
    // Maalet er naaet (eller brugeren har selv scrollet sporet i ro).
    if (this.target !== undefined && Math.abs(position - this.target) <= 1) {
      this.target = undefined;
    }
    this.prev.setAttribute('aria-disabled', String(position <= 1));
    this.next.setAttribute('aria-disabled', String(position >= max - 1));
  }

  // Hopper til naeste hele flise regnet fra det MAAL, en igangvaerende smooth
  // scroll er paa vej mod - ellers regnes et hurtigt dobbeltklik fra en
  // position midt i animationen, og et trin tabes.
  #step(direction) {
    const button = direction < 0 ? this.prev : this.next;
    if (button.getAttribute('aria-disabled') === 'true') return;
    const item = this.track.querySelector('[data-rail-item]');
    if (!item) return;
    // Ubrudt bredde: offsetWidth runder, og det driver ~1px pr. flise.
    // Gappet taeller med i skridtet - uden det driver raekken et gap pr. klik.
    // Laeses paa flisens EGEN foraelder (flex-/grid-containeren), ikke paa
    // sporet: i certificates er sporet en almindelig div, og griddet med
    // fliserne ligger et niveau nede. 'normal' -> NaN -> 0.
    const gap = parseFloat(getComputedStyle(item.parentElement).columnGap) || 0;
    const width = item.getBoundingClientRect().width + gap;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const base = this.target ?? Math.abs(this.track.scrollLeft);
    // floor/ceil efter retning: fra en position midt i en flise gaar et klik
    // altid til den naeste hele flise, aldrig halvanden. 0.01 absorberer
    // subpixel-afrunding, naar sporet allerede staar paa en flisekant.
    const index = direction > 0 ? Math.floor(base / width + 0.01) + 1 : Math.ceil(base / width - 0.01) - 1;
    this.target = Math.min(Math.max(index * width, 0), max);
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: rtl ? -this.target : this.target,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('rail-slider')) {
  customElements.define('rail-slider', RailSlider);
}
